import { type ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { Badge, Button } from "@/components/ui";
import { SubAgentRoundIoAction, TurnIoAction, type LlmIoContext } from "@/components/llm-io/LlmIoPanel";
import { cn } from "@/lib/utils";
import { mapValue, stringValue } from "@/utils/coerce";
import type { PromptBindingVersion } from "@/types/llmTrace";
import type { NormalizedTrace, PromptBinding, TraceNode } from "@/utils/trace/model";
import { PromptSourceView, type LoadPromptVersion } from "./PromptSourceView";
import { laneNodeFor } from "./SubAgentSummary";
import { TraceMetricsRow } from "./TraceMetrics";
import { TraceNodeDetail } from "./TraceNodeDetail";
import { TraceTimeline } from "./TraceTimeline";
import { TraceTokens } from "./TraceTokens";

/**
 * The node an id names, anywhere in the trace.
 *
 * Lane envelopes are checked too. A lane's own bar — a sub-agent execution, a
 * parallel batch — is not in `lane.nodes`; it is synthesized from the lane by
 * `laneNodeFor` and carries the LANE's id. It is selectable like any other
 * bar, so an id lookup that only walked the node trees would silently fail to
 * resolve exactly the bars that summarize a whole flow.
 */
function findNode(trace: NormalizedTrace, id: string): TraceNode | null {
  const walk = (nodes: TraceNode[]): TraceNode | null => {
    for (const node of nodes) {
      if (node.id === id) return node;
      const hit = walk(node.children);
      if (hit) return hit;
    }
    return null;
  };
  for (const lane of trace.lanes) {
    if (lane.id === id) {
      const envelope = laneNodeFor(lane);
      if (envelope) return envelope;
    }
    const hit = walk(lane.nodes);
    if (hit) return hit;
  }
  return null;
}

const TABS = [
  { key: "timeline", label: "타임라인" },
  { key: "tokens", label: "토큰" },
] as const;

type TabKey = (typeof TABS)[number]["key"];

/**
 * One trace, read two ways.
 *
 * Everything that describes the trace as a whole — what it cost, what ran it,
 * whether the body was replaced afterwards, what the source could not report —
 * belongs here rather than to either view, because both views are of the same
 * answer and both need it. Only the body switches.
 *
 * The two are not the same chart re-coloured: a waterfall answers "when did
 * this happen and what was it waiting on", and tokens answer "what did the
 * input consist of". A prompt's blocks have no position in time, so they have
 * no honest place on an axis.
 */
export function TraceView({
  trace,
  title = "Trace",
  showMetrics = true,
  className,
  emptyNotice,
  loadPromptVersion,
  ioContext,
  selectedNodeId,
  onSelectNode,
}: {
  trace: NormalizedTrace;
  /** `null` when the caller already heads the section, e.g. a card title. */
  title?: string | null;
  /**
   * Off when the caller shows `TraceMetricsRow` elsewhere — Conversations puts
   * it beside the message it summarises, and printing it twice on one screen
   * makes the reader check whether the two differ.
   */
  showMetrics?: boolean;
  className?: string;
  /**
   * Fetches one prompt row by the id a turn bound. Absent on a mount that has
   * no API to reach — a replay, a test — and the version badges then render as
   * plain text rather than as buttons that could never deliver.
   *
   * A callback rather than the API client itself: `trace` already arrives the
   * same way, and these components stay presentational.
   */
  loadPromptVersion?: LoadPromptVersion;
  /**
   * Where to fetch a selected turn's original request/response. Absent on a
   * mount with no API (a reconstructed replay, a test), and then no lookup is
   * offered at all.
   */
  ioContext?: LlmIoContext;
  /**
   * Shown instead of the chart when the trace came back with no turns. The
   * reason lives outside this response, so the caller supplies the wording —
   * keeping this component free of any negative-feedback types.
   */
  emptyNotice?: ReactNode;
  /**
   * Which node's detail is open, when the caller keeps that in the URL.
   *
   * Conversations does, so that **back closes the detail** and a trace step
   * can be linked to. Passing it makes the selection the caller's; omitting
   * it keeps the internal state, which is what a mount with no address of its
   * own — a modal, a replay — has to use.
   */
  selectedNodeId?: string | null;
  /** Required with `selectedNodeId`; null means "close". */
  onSelectNode?: (nodeId: string | null) => void;
}) {
  const [tab, setTab] = useState<TabKey>("timeline");
  // Owned here rather than by either reading: a node picked on the waterfall is
  // still the node you want open after switching to its token cost, and the
  // detail panel is the same panel either way.
  //
  // …unless the caller keeps it in the URL, in which case it is owned there
  // and this state goes unused. The id, not the node: the URL can only carry
  // the id, and the node is looked up from the trace it belongs to — so an id
  // left over from another answer selects nothing rather than mis-selecting.
  const controlled = onSelectNode !== undefined;
  const [internalId, setInternalId] = useState<string | null>(null);
  const selectedId = controlled ? (selectedNodeId ?? null) : internalId;
  const selected = useMemo(
    () => (selectedId === null ? null : findNode(trace, selectedId)),
    [trace, selectedId],
  );
  const toggleSelected = (node: TraceNode) => {
    const next = selectedId === node.id ? null : node.id;
    if (controlled) onSelectNode(next);
    else setInternalId(next);
  };

  // A different trace is a different set of nodes, so an internal selection
  // does not carry across one: the id would either resolve to nothing or, on
  // a re-fetch of the same answer, silently re-open a detail the reader had
  // moved on from. Where the selection lives in the URL it is the caller who
  // clears it, alongside the answer it belonged to.
  useEffect(() => {
    if (!controlled) setInternalId(null);
  }, [trace, controlled]);

  // The detail is appended below the whole chart, so on a trace tall enough to
  // scroll — which is most of them — picking a bar changed nothing the reader
  // could see. `nearest` is deliberate: it does nothing when the panel is
  // already on screen, so a split layout, where it always is, stays still.
  const detailRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!selected) return;
    detailRef.current?.scrollIntoView({ block: "nearest" });
  }, [selected]);

  // Tracked apart from `selected` on purpose. When the badges were spans inside
  // the row's button, pressing one ran the row's own toggle and shut whatever
  // detail was open — so the one control that promised more detail delivered
  // less. Two independent selections mean the source, the token cost and the
  // result can all be on screen together, which is the reason to look at all.
  const [openBinding, setOpenBinding] = useState<PromptBinding | null>(null);
  const toggleBinding = (binding: PromptBinding) =>
    setOpenBinding((current) => (current?.id === binding.id ? null : binding));
  // Keyed by prompt id and shared across rows: the same base_prompt version
  // backs every turn of an answer, so a per-row cache would refetch the same
  // 27k characters once per turn.
  const promptCache = useMemo(() => new Map<string, PromptBindingVersion>(), []);

  // request_params is snapshotted once per request and copied onto every turn,
  // so read it from the first turn and show it once.
  const requestParamBadges = useMemo(() => {
    const mainLane = trace.lanes.find((lane) => lane.kind === "main");
    const params = mapValue(mapValue(mainLane?.nodes[0]?.detail).request_params);
    return [
      ["tier", stringValue(params.model_tier, "")],
      ["model", stringValue(params.llm_model_requested, "")],
      ["effort", stringValue(params.reasoning_effort, "")],
      ["agent", stringValue(params.agent_version, "")],
    ]
      .filter(([, value]) => value && value !== "null" && value !== "undefined")
      .map(([label, value]) => `${label} ${value}`);
  }, [trace.lanes]);

  const body =
    tab === "timeline" ? (
      <TraceTimeline trace={trace} selected={selected} onSelect={toggleSelected} />
    ) : (
      <TraceTokens
        trace={trace}
        selected={selected}
        onSelect={toggleSelected}
        openBinding={openBinding}
        onOpenBinding={loadPromptVersion ? toggleBinding : undefined}
      />
    );

  return (
    <div className={cn("mt-4 min-w-0 max-w-full border-t border-border pt-3.5", className)}>
      {title !== null && <h3 className="mb-2 text-base">{title}</h3>}
      {showMetrics && <TraceMetricsRow metrics={trace.metrics} />}
      {requestParamBadges.length > 0 && (
        // Every turn in a chain carries an identical copy of request_params, so
        // this belongs to the trace, not to any one turn.
        <div className="mb-2.5 flex flex-wrap gap-2">
          {requestParamBadges.map((badge) => (
            <Badge key={badge}>{badge}</Badge>
          ))}
        </div>
      )}
      {trace.revisions.length > 1 && (
        // Not a failure and not a limit of the source, so neither a Banner nor
        // a degraded note: it is the one fact that makes the rest of the view
        // legible. The body above was replaced after it was first published, so
        // the turn drawn below is the acknowledgement's — the only turn there
        // is — and the research that produced the text ran in the lanes beside
        // it, after the answer had already gone out.
        <p className="mb-2.5 rounded-lg border border-border bg-surface-subtle p-2.5 text-xs text-muted-foreground">
          이 답변의 본문은 게시 후 {trace.revisions.length - 1}회 교체되었습니다 (
          {trace.revisions.map((revision) => revision.kind).join(" → ")}). 아래
          트레이스는 최초 게시 턴의 것이고, 최종 본문을 만든 조사는 그 뒤 비동기
          레인에서 진행됐습니다.
        </p>
      )}
      {/* The app's own tab strip, the same one NegativeFeedbackModal uses. An
          earlier pass styled these by hand and much lighter, and the result did
          not read as something you could click at all. */}
      <div role="tablist" className="mb-4 flex gap-2 border-b border-border pb-2">
        {TABS.map((entry) => (
          <Button
            key={entry.key}
            role="tab"
            aria-selected={tab === entry.key}
            variant={tab === entry.key ? "default" : "outline"}
            onClick={() => setTab(entry.key)}
          >
            {entry.label}
          </Button>
        ))}
      </div>
      {/* No turns is a different failure from no time axis: the backend only
          links a turn to its message when the answer finished cleanly, so an
          errored answer yields an empty main flow with everything else intact. */}
      {!trace.hasTurns ? (
        <div className="mb-2 grid gap-2">
          {emptyNotice ?? <p>이 답변에 연결된 실행 기록이 없습니다.</p>}
          {trace.lanes.some((lane) => lane.nodes.length > 0) && body}
        </div>
      ) : (
        body
      )}
      {trace.degraded.length > 0 && (
        // Not a Banner: these are limits of the data source, not a failure, and
        // Banner announces itself via role="alert".
        <ul className="mt-2 grid gap-1 rounded-lg border border-border bg-surface-subtle p-2.5 text-xs text-muted-foreground">
          {trace.degraded.map((note) => (
            <li key={note}>{note}</li>
          ))}
        </ul>
      )}
      {/* One wrapper so the whole of "what I just picked" scrolls into view as
          a unit — the panel and the I/O action below it are one answer. */}
      {selected && (
        <div ref={detailRef} className="scroll-mt-3">
          <TraceNodeDetail
            node={selected}
            openBinding={openBinding}
            onOpenBinding={loadPromptVersion ? toggleBinding : undefined}
          />
          {ioContext && trace.source === "llm_trace" && selected.kind === "turn"
            && trace.lanes.some((lane) => lane.nodes.includes(selected))
            && typeof selected.detail.turn_id === "string" && (
              <TurnIoAction key={`${ioContext.environment}:${selected.detail.turn_id}`}
                {...ioContext} turnId={selected.detail.turn_id} />
            )}
          {ioContext?.api.getSubAgentRoundIo && trace.source === "llm_trace" && selected.kind === "sa_model_turn"
            && typeof selected.detail.execution_id === "string" && typeof selected.detail.round_index === "number" && (
              <SubAgentRoundIoAction key={`${ioContext.environment}:${selected.id}`} {...ioContext}
                executionId={selected.detail.execution_id} roundIndex={selected.detail.round_index}
                parentTurnId={typeof selected.detail.parent_turn_id === "string" ? selected.detail.parent_turn_id : null} />
            )}
        </div>
      )}
      {openBinding && loadPromptVersion && (
        <PromptSourceView
          binding={openBinding}
          load={loadPromptVersion}
          cache={promptCache}
        />
      )}
    </div>
  );
}
